/**
 * @file src/lib/sentry.ts
 * @exports initSentry
 * @summary Initializes Sentry for the browser SPA (formerly `sentry.client.config.js`).
 * @remarks Uses `@sentry/react` with the TanStack Router browser-tracing integration. Only enabled
 *          in production builds; release/source maps are handled by `@sentry/vite-plugin`.
 */

import * as Sentry from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";

export const initSentry = (router: AnyRouter): void => {
	Sentry.init({
		dsn: "https://5b87427a93844bd2abe66046ecdcba72@o1204178.ingest.sentry.io/4504136659828736",
		// Adjust this value in production, or use tracesSampler for greater control
		tracesSampleRate: 1.0,
		// Note: if you want to override the automatic release value, do not set a
		// `release` value here - use the environment variable `SENTRY_RELEASE`, so
		// that it will also get attached to your source maps

		// only enabled sentry in production
		enabled: process.env.NODE_ENV === "production",
		ignoreErrors: [
			"ResizeObserver loop limit exceeded",
			"ResizeObserver loop completed with undelivered notifications.", // FireFox version
		],

		// This sets the sample rate to be 10%. You may want this to be 100% while
		// in development and sample at a lower rate in production
		replaysSessionSampleRate: 0.1,
		// If the entire session is not sampled, use the below sample rate to sample
		// sessions when an error occurs.
		replaysOnErrorSampleRate: 1.0,

		integrations: [
			Sentry.tanstackRouterBrowserTracingIntegration(router),
			Sentry.replayIntegration({
				unmask: [".sentry-unmask, [data-sentry-unmask]"],
				unblock: [".sentry-unblock, [data-sentry-unblock]"],
			}),
		],
	});
};
